import type { EmployeeResponse } from '@/types/employee';
import type { CustomerResponse } from '@/types/customer';
import type { ProductResponse } from '@/types/product';
import type { StoreResponse } from '@/types/store';
import type { SupplierResponse } from '@/types/supplier';

import type { EntityOptionResponse } from '@/types/entityOption';

export function formatEntityOptionLabel(option: EntityOptionResponse, short = false): string {
  if (short && option.code) {
    return option.code;
  }
  const base = option.label || `#${option.id}`;
  if (option.sublabel) {
    return `${base} (${option.sublabel})`;
  }
  if (option.code && !short) {
    return `${base} · ${option.code}`;
  }
  return base;
}

export function formatEntityOptionShort(option: EntityOptionResponse): string {
  return option.code || option.label || `#${option.id}`;
}

/** Employee-style display: `CODE - Full Name` (code omitted when missing). */
export function formatEntityCodeName(option: EntityOptionResponse): string {
  const name = option.label?.trim() || `#${option.id}`;
  const code = option.code?.trim();
  return code ? `${code} - ${name}` : name;
}

export function formatEmployeeLabel(employee: Pick<EmployeeResponse, 'id' | 'employeeCode' | 'firstName' | 'lastName' | 'email'>): string {
  const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ');
  const code = employee.employeeCode?.trim();
  const base = code ? `${code} — ${name}` : name || `Employee #${employee.id}`;
  return employee.email ? `${base} (${employee.email})` : base;
}

export function formatEmployeeShort(employee: Pick<EmployeeResponse, 'id' | 'employeeCode' | 'firstName' | 'lastName'>): string {
  const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ');
  return employee.employeeCode ? `${employee.employeeCode} — ${name}` : name || `#${employee.id}`;
}

export function formatCustomerLabel(customer: Pick<CustomerResponse, 'id' | 'name' | 'email' | 'phone'>): string {
  const contact = customer.email || customer.phone;
  return contact ? `${customer.name} (${contact})` : customer.name;
}

export function formatSupplierLabel(supplier: Pick<SupplierResponse, 'id' | 'name' | 'email' | 'phone' | 'gstNumber'>): string {
  const parts = [supplier.name];
  if (supplier.gstNumber) parts.push(supplier.gstNumber);
  else if (supplier.email) parts.push(supplier.email);
  else if (supplier.phone) parts.push(supplier.phone);
  return parts.join(' · ');
}

export function formatSupplierShort(supplier: Pick<SupplierResponse, 'id' | 'name'>): string {
  return supplier.name || `#${supplier.id}`;
}

export function formatProductLabel(product: Pick<ProductResponse, 'id' | 'name' | 'sku' | 'barcode'>): string {
  const sku = product.sku?.trim();
  const base = sku ? `${product.name} · ${sku}` : product.name || `Product #${product.id}`;
  return product.barcode ? `${base} · ${product.barcode}` : base;
}

export function formatProductShort(product: Pick<ProductResponse, 'id' | 'name' | 'sku'>): string {
  const sku = product.sku?.trim();
  return sku ? `${sku} — ${product.name}` : product.name || `#${product.id}`;
}

export function formatStoreLabel(store: Pick<StoreResponse, 'id' | 'name' | 'code'>): string {
  return store.code ? `${store.name} (${store.code})` : store.name || `#${store.id}`;
}

export function formatStoreShort(store: Pick<StoreResponse, 'id' | 'name' | 'code'>): string {
  return store.code || store.name || `#${store.id}`;
}

export function formatPurchaseRequestLabel(row: {
  id: number;
  requestNumber?: string | null;
  requestStatus?: string | null;
}): string {
  const num = row.requestNumber?.trim();
  return num ? `${num}${row.requestStatus ? ` · ${row.requestStatus}` : ''}` : `PR #${row.id}`;
}

export function formatPurchaseOrderLabel(row: {
  id: number;
  poNumber?: string | null;
  poStatus?: string | null;
}): string {
  const num = row.poNumber?.trim();
  return num ? `${num}${row.poStatus ? ` · ${row.poStatus}` : ''}` : `PO #${row.id}`;
}

export function entityIdLabel(id: number | string | null | undefined, name?: string | null): string {
  if (name) return name;
  if (id == null || id === '') return '—';
  return `#${id}`;
}
